import React, { useState } from "react";
import { Form, Input, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import * as assetAction from "store/Actions/asset";
import { getFormattedAmount } from "Shared/Utils";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { Tokens } from "Shared/Tokens";
import { mpTapPreview } from "Shared/Mixpanel/AssetsManager";

const PortfolioOnBuy = (props) => {
    const { coinInfo = {} } = props;
    const history = useHistory();
    const dispatch = useDispatch();
    const selectedPortfolio = useSelector(
        (state) => state.portfolios.selectedPortfolio
    );
    const asset = useSelector((state) => state.assets.selectedAsset);
    const settings = useSelector((state) => state.settings || {});
    const currSymbol = getCurrencySymbol(settings?.currency || "INR");
    const holdings = asset?.portfolio?.latest?.numberOfCoins || 0;
    const avgBuyPrice = asset?.portfolio?.latest?.marketPrice || 0;
    const currentMarketPrice = coinInfo?.current_price || 0;
    const coinSymbol = String(
        asset?.symbol || coinInfo?.symbol || ""
    ).toUpperCase();

    const [form] = Form.useForm();
    const initialInvestment = Number(asset?.portfolio?.latest?.investment || 0);
    const [formData, setFormData] = useState({
        currentInvestment: initialInvestment,
        averagePPC: Number(avgBuyPrice || 0),
        numberOfTokens: "",
        marketPrice: Number(currentMarketPrice || avgBuyPrice || 0),
    });
    const [disabled, setDisabled] = useState(true);
    const handleValueChange = (value) => {
        const _formData = {
            ...formData,
            ...value,
        };
        const qty = Number(_formData.numberOfTokens);
        const price = Number(_formData.marketPrice);
        setDisabled(!(qty > 0 && price > 0));
        setFormData(_formData);
    };
    const onFinish = (values) => {
        const currentInvestment = Number(
            values.currentInvestment ?? initialInvestment
        );
        const averagePPC = Number(values.averagePPC ?? avgBuyPrice);
        const numberOfTokens = Number(values.numberOfTokens);
        const marketPrice = Number(values.marketPrice);
        const newInvestment = numberOfTokens * marketPrice;
        dispatch(
            assetAction.buy({
                currentInvestment,
                averagePPC,
                newInvestment,
                numberOfTokens,
                marketPrice,
            })
        );
        mpTapPreview({ type: "buy" });
        history.push(
            `/portfolios/${selectedPortfolio.name}/assets/${asset.id}/preview/buy`
        );
    };

    const onFinishFailed = (errorInfo) => {
        console.log("Failed:", errorInfo);
    };

    const summaryMetrics = [
        {
            label: "Holdings",
            value: `${getFormattedAmount(holdings)}${
                coinSymbol ? ` ${coinSymbol}` : ""
            }`,
        },
        {
            label: "Avg buy price",
            value: `${currSymbol}${getFormattedAmount(avgBuyPrice)}`,
        },
        {
            label: "Market price",
            value: `${currSymbol}${getFormattedAmount(currentMarketPrice)}`,
        },
    ];

    const estimatedBuyTotal =
        (Number(formData.numberOfTokens) || 0) *
        (Number(formData.marketPrice) || 0);

    return (
        <div
            style={{
                maxWidth: Tokens.containerMaxWidth,
                margin: "0 auto",
                width: "100%",
            }}
        >
            <div
                style={{
                    backgroundColor: Tokens.card,
                    border: `1px solid ${Tokens.hairline}`,
                    borderRadius: Tokens.radiusCard,
                    padding: "12px 16px",
                    marginBottom: "20px",
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "12px",
                }}
            >
                {summaryMetrics.map((metric) => (
                    <div key={metric.label} style={{ minWidth: 0 }}>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                                marginBottom: "4px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {metric.label}
                        </div>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                                fontSize: "13px",
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {metric.value}
                        </div>
                    </div>
                ))}
            </div>
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    currentInvestment: getFormattedAmount(
                        initialInvestment,
                        false
                    ),
                    averagePPC: getFormattedAmount(avgBuyPrice, false),
                    marketPrice: getFormattedAmount(
                        currentMarketPrice || avgBuyPrice,
                        false
                    ),
                }}
                onValuesChange={handleValueChange}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                name="get-portfolio-on-buy"
            >
                <Form.Item
                    label="Current investment"
                    name="currentInvestment"
                    hidden
                >
                    <Input type="number" />
                </Form.Item>
                <Form.Item label="Average buy price" name="averagePPC" hidden>
                    <Input type="number" />
                </Form.Item>
                <Form.Item
                    label={`Quantity to buy${
                        coinSymbol ? ` (${coinSymbol})` : ""
                    }`}
                    name="numberOfTokens"
                >
                    <Input
                        placeholder="e.g. 0.05"
                        type="number"
                        value={formData.numberOfTokens}
                        step="any"
                        min="0"
                        size="large"
                        style={{
                            fontSize: "16px",
                            fontFamily: Tokens.fontMono,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    />
                </Form.Item>
                <Form.Item
                    label={`Buy price per coin (${
                        settings?.currency || "USDT"
                    })`}
                    name="marketPrice"
                >
                    <Input
                        placeholder=""
                        type="number"
                        value={formData.marketPrice}
                        step="any"
                        min="0"
                        size="large"
                        style={{
                            fontSize: "16px",
                            fontFamily: Tokens.fontMono,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    />
                </Form.Item>
                {estimatedBuyTotal > 0 && (
                    <div
                        data-testid="buy-estimated-total"
                        style={{
                            backgroundColor: Tokens.card,
                            border: `1px solid ${Tokens.hairline}`,
                            borderRadius: Tokens.radiusControl,
                            padding: "10px 14px",
                            marginBottom: "16px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <span
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "13px",
                            }}
                        >
                            Estimated total cost
                        </span>
                        <span
                            style={{
                                color: Tokens.textPrimary,
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                                fontWeight: 600,
                                fontSize: "14px",
                            }}
                        >
                            {currSymbol}
                            {getFormattedAmount(estimatedBuyTotal, true, 2)}
                        </span>
                    </div>
                )}
                <Form.Item>
                    <Button
                        disabled={disabled}
                        type="primary"
                        htmlType="submit"
                        block
                        size="large"
                    >
                        Preview Buy
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default PortfolioOnBuy;
